// Shared domain objects that appear inside more than one REST body or WS frame
// (e.g. a friend request is both a REST resource and a real-time WS mirror).
// Defined once here to avoid divergence between the two surfaces.

import { z } from 'zod';
import {
  AvatarSeed,
  CallId,
  DisplayName,
  Ed25519PublicKey,
  Timestamp,
  Token,
  Uuid,
} from './primitives.js';

/** Friend-request lifecycle status (mirrors the `friend_requests.status` check). */
export const FriendRequestStatus = z.enum(['pending', 'accepted', 'declined']);
export type FriendRequestStatus = z.infer<typeof FriendRequestStatus>;

/** A friend request, as surfaced over REST and mirrored by `friend.*` WS frames. */
export const FriendRequest = z
  .object({
    id: Uuid,
    fromUserId: Uuid,
    toUserId: Uuid,
    status: FriendRequestStatus,
    /** Requester's self-chosen name (the target sees UUID + name). */
    fromDisplayName: DisplayName.optional(),
    /** Target's name, populated once known. */
    toDisplayName: DisplayName.optional(),
    createdAt: Timestamp,
  })
  .strict();
export type FriendRequest = z.infer<typeof FriendRequest>;

/**
 * Presence detail: `app` = a live app socket (the friend is in Merkury right
 * now, green); `reachable` = no app socket but the device can still be made
 * to ring — a native stay-reachable socket or a push subscription (yellow);
 * `offline` = neither (gray).
 */
export const PresenceStatus = z.enum(['app', 'reachable', 'offline']);
export type PresenceStatus = z.infer<typeof PresenceStatus>;

/** An accepted friend, incl. their identity public key and presence snapshot. */
export const Friend = z
  .object({
    userId: Uuid,
    publicKey: Ed25519PublicKey,
    displayName: DisplayName,
    /** Back-compat boolean: true ⇔ `presence === 'app'`. */
    online: z.boolean(),
    presence: PresenceStatus.optional(),
    lastSeenAt: Timestamp.optional(),
    /** Deterministic-avatar seed the friend picked; absent ⇔ never chosen. */
    avatarSeed: AvatarSeed.optional(),
  })
  .strict();
export type Friend = z.infer<typeof Friend>;

/** A logged-in app install (see the `sessions` table). */
export const SessionKind = z.enum(['web', 'android', 'ios']);
export type SessionKind = z.infer<typeof SessionKind>;

/**
 * A registered passkey (WebAuthn credential), as listed in Settings. Passkeys
 * sign an already-provisioned device into the server; they do NOT restore the
 * E2E identity (docs/03 §2 — the recovery phrase is still the account root).
 * The credential's public key / sign counter never leave the server, so only
 * this display summary is on the wire.
 */
export const PasskeySummary = z
  .object({
    id: Uuid,
    label: z.string().max(128),
    createdAt: Timestamp,
    lastUsedAt: Timestamp.optional(),
  })
  .strict();
export type PasskeySummary = z.infer<typeof PasskeySummary>;

export const Session = z
  .object({
    id: Uuid,
    kind: SessionKind,
    label: z.string().max(128),
    createdAt: Timestamp,
    lastSeenAt: Timestamp.optional(),
    /** True for the caller's own session. */
    current: z.boolean(),
  })
  .strict();
export type Session = z.infer<typeof Session>;

/** Outcome of a logged call (mirrors `call_log.outcome`). */
export const CallOutcome = z.enum(['completed', 'missed', 'declined', 'failed']);
export type CallOutcome = z.infer<typeof CallOutcome>;

/** One entry of the caller's own call history (metadata only). */
export const CallLogEntry = z
  .object({
    id: CallId,
    peerUserId: Uuid,
    direction: z.enum(['incoming', 'outgoing']),
    startedAt: Timestamp,
    endedAt: Timestamp.optional(),
    outcome: CallOutcome,
    /** Missed entries carry `seen:false` until acknowledged. */
    seen: z.boolean(),
  })
  .strict();
export type CallLogEntry = z.infer<typeof CallLogEntry>;

/** The token bundle returned by every successful login / refresh. */
export const TokenBundle = z
  .object({
    accessToken: Token,
    refreshToken: Token,
    /** Access-token lifetime in seconds (15 min per docs/02 §2). */
    expiresIn: z.number().int().positive(),
    /** The account session (`sid`) these tokens belong to. */
    sessionId: Uuid,
  })
  .strict();
export type TokenBundle = z.infer<typeof TokenBundle>;

/**
 * Error codes carried by REST error bodies and `error` WS frames. The three
 * named in docs/02 §4 are `not_friends`, `callee_offline`, `rate_limited`; the
 * rest are defined here so both implementations share one authoritative set.
 */
export const ErrorCode = z.enum([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'not_friends',
  'callee_offline',
  'busy',
  'invalid_signature',
  // Key-login (challenge/verify) on an account WITH a password set must also
  // prove the password (docs/03 §3a). Returned identically whether the
  // password proof was absent or wrong — never a has-password oracle.
  'password_required',
  'server_error',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

/** Shared error body: `error` WS payload and REST error responses. */
export const ApiError = z
  .object({
    code: ErrorCode,
    message: z.string(),
    /** Optional opaque correlation ref (never a token/UUID/IP). */
    ref: z.string().optional(),
  })
  .strict();
export type ApiError = z.infer<typeof ApiError>;

/**
 * Push notification payload (docs/06 §4, docs/02 §4): the ONLY data a push
 * carries — no names, keys, or tokens. Parsed defensively by the service
 * worker; the server contract-tests what it dispatches against this schema.
 *
 * The `type` discriminator selects the client behaviour, all keyed to `callId`:
 *   - `incoming_call` — ring the device for a pending invite;
 *   - `missed_call`   — the ring is over AND the call went unanswered (caller
 *                       gave up, or it timed out): stop ringing, show a missed
 *                       call;
 *   - `call_cancelled`— the ring is over because ANOTHER of the callee's own
 *                       sessions handled it (answered or declined elsewhere):
 *                       stop ringing WITHOUT a missed-call notification. Lets a
 *                       device rung only via push (no live socket to receive
 *                       the `call.end{answered_elsewhere}` frame) stop instead
 *                       of ringing to its safety cap.
 */
export const PushPayload = z
  .object({
    type: z.enum(['incoming_call', 'missed_call', 'call_cancelled']),
    callId: Uuid,
    /**
     * Server send time, epoch milliseconds — the end-to-end freshness guard
     * against stale/replayed pushes (a censorship-grade network, or ntfy's
     * message cache, can deliver a queued `incoming_call` hours late; without
     * this the phone rings for a call that no longer exists). Receivers DROP an
     * `incoming_call` older than the ring window (plus a clock-skew allowance)
     * instead of ringing. Optional: an older server omits it, and a payload
     * WITHOUT it is never dropped (rings as before). Not identifying — a
     * coarse timestamp reveals nothing the push infrastructure cannot already
     * time-stamp itself (docs/03 §6).
     */
    sentAt: z.number().int().nonnegative().optional(),
  })
  .strict();
export type PushPayload = z.infer<typeof PushPayload>;
